import { get } from 'lodash-es';
import Badge from './Badge';
import { emitter, PRODUCT_ADDED_TO_CART } from '../event-bus';
import { AddReferenceError, cartWorkflowHandler } from '@phoenix/api/CartWorkflowHandler';
import { showPanel } from '@phoenix/helpers/panel-helper';
import i18n from '../i18n';
import {
  invalidateOrderLinesSync,
  orderLinesLastSyncLocalStorageHelper,
  orderLinesLocalStorageHelper,
} from '@phoenix/helpers/product-localstorage-helper';

export default class Bag {
  btns: Array<HTMLInputElement>;
  desktopBadge: Badge;
  mobileBadge: Badge;
  timeoutRemovePromotionBlock: ReturnType<typeof setTimeout>;
  addToBagClickCallback: (() => void) | null;

  constructor(
    addToBagClickCallback: (() => void) | null = null,
    scope: ParentNode = document,
    selector = '.btn-add-to-cart',
  ) {
    this.btns = Array.from(scope.querySelectorAll(selector) as NodeListOf<HTMLInputElement>);
    this.addToBagClickCallback = addToBagClickCallback;
    this.handleAddToBagClick = this.handleAddToBagClick.bind(this);
    this.btns.forEach((btn) => {
      btn.addEventListener('click', this.handleAddToBagClick);
    });
  }

  handleAddToBagClick(event: MouseEvent) {
    event.preventDefault();
    const btn = event.currentTarget as HTMLInputElement;
    const { productReference, productOptions, productExtraData } = btn.dataset;
    if (productReference) {
      this.add(productReference, JSON.parse(productOptions ?? '{}'), JSON.parse(productExtraData ?? '{}'));
    } else if (this.addToBagClickCallback !== null) {
      this.addToBagClickCallback();
    }
  }

  removeEventListeners() {
    this.btns.forEach((btn) => btn.removeEventListener('click', this.handleAddToBagClick));
  }

  activate() {
    this.btns.forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.classList.remove('btn--disabled', 'hidden', 'sgphx-hidden');
    });
  }

  disable() {
    this.btns.forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('btn--disabled');
    });
  }

  hide() {
    this.btns.forEach((btn) => btn.classList.add('hidden', 'sgphx-hidden'));
  }

  setReference(reference: string) {
    this.btns.forEach((btn) => btn.setAttribute('data-product-reference', reference));
  }

  async add(reference: string, options: { redirectUrl?: string; shouldOpenCartPanel?: boolean }, extraData = {}) {
    const redirectUrl = options.redirectUrl || null;
    const shouldOpenCartPanel = options.shouldOpenCartPanel ?? true;

    this.btns.forEach((btn) => window.Toolkit.helpers.buttons.addLoadingState(btn));

    let addToBagErrorMessage = null;
    let cartProductsRemoved: boolean;
    try {
      const result = await cartWorkflowHandler.addReference(reference);
      cartProductsRemoved = result.cartProductsRemoved;

      const shoppingTotalCount = Bag.saveShoppingBagTotalFromResponse({
        cart: result.cart,
      });

      emitter.emit('shopping-badge-update', shoppingTotalCount);
    } catch (error) {
      addToBagErrorMessage = (error as Error).message || i18n.global.t('Error.errorWhileAddingProduct');
      cartProductsRemoved = error instanceof AddReferenceError && error.cartProductsRemoved;
    }

    if (cartProductsRemoved) {
      Phoenix.toaster.notify({
        type: 'info',
        text: i18n.global.t('Checkout.cartCancelled'),
      });
    }

    if (addToBagErrorMessage !== null) {
      this.btns.forEach((btn) => window.Toolkit.helpers.buttons.removeLoadingState(btn));
      return Phoenix.toaster.notify({
        type: 'danger',
        text: addToBagErrorMessage,
      });
    }

    tracking.pushEvent(tracking.getAddToCartTracking());

    emitter.emit(PRODUCT_ADDED_TO_CART, {
      reference,
      extraData,
    });

    if (redirectUrl) {
      invalidateOrderLinesSync();
      window.location.href = redirectUrl;
      return;
    }

    if (shouldOpenCartPanel) {
      showPanel('cart-panel');
    }

    this.btns.forEach((btn) => window.Toolkit.helpers.buttons.removeLoadingState(btn));
    // If the user is on mobile, we hide the size selector
    const sizeStep1 = document.getElementById('choose-a-size-step-1');
    const sizeStep2 = document.getElementById('choose-a-size-step-2');
    if (sizeStep1) {
      sizeStep1.classList.remove('hidden');
    }
    if (sizeStep2) {
      sizeStep2.classList.add('hidden');
    }

    return true;
  }

  static saveShoppingBagTotalFromResponse(data: object) {
    const shoppingBagTotal = get(data, ['cart', 'items'], []).length;
    orderLinesLocalStorageHelper.save(shoppingBagTotal);
    orderLinesLastSyncLocalStorageHelper.save(Date.now());
    return shoppingBagTotal;
  }
}
